import { AnimatePresence, motion } from "framer-motion";
import { FC, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { ChatBubbleLeftIcon, EnvelopeIcon, PhoneIcon, UserIcon } from "@heroicons/react/24/outline";
import { FaUser, FaEnvelope, FaPhone, FaCommentDots, FaPaperPlane } from "react-icons/fa";
import { zodResolver } from "@hookform/resolvers/zod";

import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import Textarea from "shared/Textarea/Textarea";
import { useAppSelector } from "hooks/hooks";

interface ContactFormProps {
  onSubmit: SubmitHandler<FormData>;
}

const formSchema = z.object({
  nom: z.string().min(1, { message: "Le nom et le prénom sont obligatoires." }),
  email: z.string().email({ message: "L'adresse e-mail est obligatoire." }),
  message: z.string().min(15, { message: "Le message doit contenir au moins 15 caractères." }),
  phone: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const ContactForm: FC<ContactFormProps> = ({ onSubmit }) => {
  const [loading, setIsLoading] = useState<boolean>(false);

  const contactState = useAppSelector((state) => state.featureContact);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { nom: "", email: "", message: "", phone: "" },
    mode: "onSubmit",
  });

  useEffect(() => {
    switch (contactState.loading) {
      case "pending":
        setIsLoading(true);
        break;
      case "succeeded":
        setIsLoading(false);
        reset();
        break;
      case "failed":
        setIsLoading(false);
        break;
    }
  }, [contactState.loading]);

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      className="bg-white dark:bg-neutral-800 rounded-3xl p-8 shadow-lg"
    >
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2 flex items-center">
          <FaPaperPlane className="w-6 h-6 mr-3 text-pink-500" />
          Envoyez-nous un message
        </h3>
        <p className="text-neutral-600 dark:text-neutral-400">
          Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label className="flex items-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center mr-3">
              <FaUser className="w-4 h-4 text-pink-600 dark:text-pink-400" />
            </div>
            Nom et Prénom *
          </label>
          <Input
            type="text"
            placeholder="Votre nom complet"
            className={`w-full rounded-2xl border-2 transition-all duration-300 ${errors.nom ? "border-red-500 focus:border-red-500" : "border-neutral-200 dark:border-neutral-600 focus:border-pink-500"} focus:ring-pink-500/20 dark:bg-neutral-700 dark:text-white px-4 py-3`}
            {...register("nom")}
          />
          <AnimatePresence>
            {errors.nom && (
              <motion.p
                className="mt-1 text-xs text-red-500"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {errors.nom.message}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <div>
          <label className="flex items-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center mr-3">
              <FaEnvelope className="w-4 h-4 text-pink-600 dark:text-pink-400" />
            </div>
            Adresse email *
          </label>
          <Input
            type="email"
            placeholder="votre@email.com"
            className={`w-full rounded-2xl border-2 transition-all duration-300 ${errors.email ? "border-red-500 focus:border-red-500" : "border-neutral-200 dark:border-neutral-600 focus:border-pink-500"} focus:ring-pink-500/20 dark:bg-neutral-700 dark:text-white px-4 py-3`}
            {...register("email")}
          />
          <AnimatePresence>
            {errors.email && (
              <motion.p
                className="mt-1 text-xs text-red-500"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {errors.email.message}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <div>
          <label className="flex items-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center mr-3">
              <FaPhone className="w-4 h-4 text-pink-600 dark:text-pink-400" />
            </div>
            Numéro de téléphone (optionnel)
          </label>
          <Input
            type="tel"
            placeholder="+33 6 12 34 56 78"
            className="w-full rounded-2xl border-2 border-neutral-200 dark:border-neutral-600 focus:border-pink-500 focus:ring-pink-500/20 dark:bg-neutral-700 dark:text-white px-4 py-3 transition-all duration-300"
            {...register("phone")}
          />
        </div>

        <div>
          <label className="flex items-center text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">
            <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900/30 rounded-lg flex items-center justify-center mr-3">
              <FaCommentDots className="w-4 h-4 text-pink-600 dark:text-pink-400" />
            </div>
            Votre message *
          </label>
          <Textarea
            placeholder="Décrivez votre demande en détail..."
            className={`w-full rounded-2xl border-2 transition-all duration-300 ${errors.message ? "border-red-500 focus:border-red-500" : "border-neutral-200 dark:border-neutral-600 focus:border-pink-500"} focus:ring-pink-500/20 dark:bg-neutral-700 dark:text-white px-4 py-3 resize-none`}
            rows={6}
            {...register("message")}
          />
          <AnimatePresence>
            {errors.message && (
              <motion.p
                className="mt-1 text-xs text-red-500"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {errors.message.message}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <ButtonPrimary
            type="submit"
            loading={loading}
            className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white font-semibold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Envoi en cours...
              </>
            ) : (
              <>
                <FaPaperPlane className="w-5 h-5 mr-2" />
                Envoyer le message
              </>
            )}
          </ButtonPrimary>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default ContactForm;
